import React, { useMemo, useState } from "react";

import { ButtonStandard, ModalBase, MultipleSelectList, Spinner, Text } from "@performyard/py-components";
import type { Option } from "@performyard/py-components";

import { NoResultsFound } from "../../../components/Modals/ModalSelectEmployees/NoResultsFound";

import { useSignatureRequestTemplates } from "../../../api/hooks/defs/signature-request-templates";

/**
 * The list contract's max page size, and it has no name filter — so the picker fetches
 * one page and searches it client-side. Companies past this many templates get a
 * truncated list, which the modal says out loud rather than leaving one silently missing.
 */
const TEMPLATE_LIMIT = 100;

/**
 * The list itself hugs its rows -- `MultipleSelectList` sizes the scroll container to
 * `min(rows * 32, 320)` -- so the modal grows with the result count up to the 320px max.
 * This floor is reserved only for the loading and empty states, whose boxes are absolutely
 * positioned over the list area and would otherwise have no height to sit in.
 */
const LIST_PLACEHOLDER_HEIGHT = 368;

export type SignatureTemplateSelection = {
	id: string;
	name: string;
	signerCount?: number;
};

/**
 * Renders one option as the Figma frames draw it: a 32px row of plain text on the
 * alternating neutral-0/neutral-1 background `MultipleSelectList` paints, with the
 * design's 8px inline padding. There is no radio or checkbox — the whole row is the hit
 * target, and the chosen one is called out with a primary-1 fill and semi-bold text.
 */
const renderTemplateOption = (option: Option<string>, isSelected: boolean, onToggle: () => void) => (
	<button
		type="button"
		role="option"
		aria-selected={isSelected}
		onClick={onToggle}
		className={`flex h-full w-full min-w-0 cursor-pointer items-center border-none px-2 text-left ${
			isSelected ? "bg-primary-1" : "bg-transparent hover:bg-neutral-2"
		}`}
		data-testid={`signature-template-option-${option.value}`}
	>
		<Text
			size="paragraph-3"
			weight={isSelected ? "semi-bold" : "regular"}
			color="neutral-9"
			wrap={false}
			dangerouslySetClassName="min-w-0 py-text-truncate"
		>
			{option.label}
		</Text>
	</button>
);

/**
 * Modal for attaching a signature request document to a workflow template to-do. Like
 * the legacy picker, only signature request templates are offered: a template to-do has
 * no subject yet, so raw documents (whose signers are chosen in the signing wizard at
 * launch) can't be set up from here.
 *
 * Built on the same `MultipleSelectList` the design names — and that the launch wizard's
 * copy of this modal uses — so it inherits the striped rows, thin scrollbar and search
 * field the employee/form/group selection modals use. Selection is single-value, and the
 * footer's Select button confirms so a stray click can't attach the wrong document.
 */
const SignatureTemplatePickerModal = ({
	onClose,
	onSelect,
}: {
	onClose: () => void;
	onSelect: (selection: SignatureTemplateSelection) => void;
}) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [selected, setSelected] = useState<SignatureTemplateSelection | null>(null);

	const { data, isLoading } = useSignatureRequestTemplates(
		{
			limit: TEMPLATE_LIMIT,
			sortBy: "name",
			sortOrder: "asc",
		},
		{ staleTime: 0, refetchOnMount: "always" }
	);

	const templates = useMemo(() => data?.signatureRequestTemplates ?? [], [data]);
	const truncated = (data?.totalCount ?? 0) > templates.length;

	const search = searchTerm.trim().toLowerCase();
	const options = useMemo<Option<string>[]>(
		() =>
			templates
				.filter((template) => !search || template.name.toLowerCase().includes(search))
				.map((template) => ({ label: template.name, value: template._id })),
		[templates, search]
	);

	/**
	 * `MultipleSelectList` toggles values in and out of an array. Keeping the one value the
	 * user just added collapses that to single-select; clicking the already-selected row
	 * yields no new value, which leaves the current selection in place rather than clearing it.
	 */
	const handleChange = (values: string[]) => {
		const nextId = values.find((value) => value !== selected?.id);
		if (!nextId) return;

		const template = templates.find(({ _id }) => _id === nextId);
		if (template) setSelected({ id: template._id, name: template.name, signerCount: template.actorCount });
	};

	const noResults = !isLoading && options.length === 0;

	return (
		<ModalBase
			isOpen
			onClose={onClose}
			dangerouslySetClassName="w-[432px] max-w-[90vw] max-h-[600px] !overflow-hidden"
		>
			<div className="px-6 pb-4">
				<div className="text-center">
					<Text size="sub-heading" weight="semi-bold">
						Select Signature Template
					</Text>
				</div>
			</div>

			<div className="flex-1 px-6">
				<div
					className="relative [&_[role=listbox]]:![scrollbar-color:#9197A1_#FFFFFF]"
					style={isLoading || noResults ? { minHeight: `${LIST_PLACEHOLDER_HEIGHT}px` } : undefined}
					data-testid="signature-template-list"
				>
					<MultipleSelectList<string>
						options={isLoading ? [] : options}
						value={selected ? [selected.id] : []}
						onChange={handleChange}
						searchPlaceholder="Search Signature Templates..."
						searchValue={searchTerm}
						onSearchChange={setSearchTerm}
						renderOption={renderTemplateOption}
						virtualizationConfig={{ itemHeight: 32, maxHeight: 320 }}
					/>

					{noResults &&
						(search ? (
							<NoResultsFound />
						) : (
							<div className="absolute inset-x-0 top-14 flex h-[320px] flex-col items-center justify-center border border-solid border-neutral-3 bg-neutral-0 px-6 text-center">
								<Text size="paragraph-3" weight="regular" color="neutral-6">
									No signature request templates found. Create one from the Signature Requests page
									first.
								</Text>
							</div>
						))}

					{isLoading && (
						<div className="absolute inset-0 top-14 flex items-center justify-center bg-neutral-0">
							<Spinner size={32} />
						</div>
					)}
				</div>

				{truncated && (
					<div className="mt-2">
						<Text size="caption" weight="regular" color="neutral-6">
							{`Showing the first ${TEMPLATE_LIMIT} templates by name. Manage templates from the Signature Requests page if the one you need isn't listed.`}
						</Text>
					</div>
				)}
			</div>

			<div className="sticky bottom-0 bg-neutral-0 px-6 pt-6 pb-6">
				<div className="flex justify-center gap-2">
					<div className="w-fit">
						<ButtonStandard variant="outline" color="neutral" size="large" onClick={onClose}>
							Cancel
						</ButtonStandard>
					</div>
					<div className="w-fit">
						<ButtonStandard
							variant="solid"
							color="primary"
							size="large"
							disabled={!selected}
							onClick={() => selected && onSelect(selected)}
							data-testid="signature-template-select-button"
						>
							Select
						</ButtonStandard>
					</div>
				</div>
			</div>
		</ModalBase>
	);
};

export default SignatureTemplatePickerModal;
